/**
 * Czas pracy — widok HR (spec 01 §6).
 *
 * Pasek okresu ze stanem, cztery karty wyjątków i kalendarz miesiąca. Wyjątki
 * liczy serwer z realnych wpisów; „brak" oznacza dzień roboczy bez ewidencji,
 * a nie kolorowy marker z makiety [D19].
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  ChevronLeft,
  ChevronRight,
  EmptyState,
  InfoBanner,
  Spinner,
  StatCard,
  StatusPill,
  cx,
  useToast,
} from '@k1p4/ui'
import { useHasPermission } from '../../auth/AuthProvider'
import { formatDate } from '@k1p4/i18n'
import { shiftMonth } from '@k1p4/shared'
import { useFormat } from '../../format'
import { api, ApiError, type TimeApprovalEntry, apiMonth } from '../../api/client'
import styles from './Time.module.css'

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/** Ile pustych komórek przed pierwszym dniem — kalendarz zaczyna się od poniedziałku. */
function leadingBlanks(firstDate: string): number {
  const day = new Date(`${firstDate}T00:00:00Z`).getUTCDay()
  return day === 0 ? 6 : day - 1
}

export function Time() {
  const { t } = useTranslation()
  const fmt = useFormat()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [month, setMonth] = useState(() => apiMonth())
  const [error, setError] = useState<string | null>(null)
  const canClosePeriods = useHasPermission('Czas pracy : Zamykanie okresów')
  const canAcceptEntries = useHasPermission('Czas pracy : Akceptacja')
  const canApprove = canAcceptEntries || canClosePeriods

  const period = useQuery({
    queryKey: ['time', 'period', month],
    queryFn: () => api.timePeriod(month),
  })

  const pendingEntries = useQuery({
    queryKey: ['time', 'entries', month],
    queryFn: () => api.timeEntries(month),
  })

  const approve = useMutation({
    mutationFn: (id: string) => api.approveTimeEntry(id),
    onSuccess: () => {
      toast.show(t('hrTime.entryApproved'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['time'] })
    },
    onError: () => setError('hrTime.approveError'),
  })

  /* Akceptacja wszystkich wpisów jednej osoby (fala 7b, grupa 135, D103). */
  const approveAll = useMutation({
    mutationFn: (ids: readonly string[]) => api.approveTimeEntries(ids),
    onSuccess: (result) => {
      toast.show(t('hrTime.approvedBulk', { count: result.approved }), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['time'] })
    },
    onError: () => setError('hrTime.approveError'),
  })

  /* Zamknięcie z niezatwierdzonymi wpisami czeka na potwierdzenie (grupa 136). */
  const [unapprovedToClose, setUnapprovedToClose] = useState<number | null>(null)

  const change = useMutation({
    mutationFn: ({ action, acknowledge }: { action: 'submit' | 'close' | 'reopen'; acknowledge?: boolean }) =>
      api.changeTimePeriod(month, action, acknowledge),
    onSuccess: (_result, { action }) => {
      setUnapprovedToClose(null)
      toast.show(
        t(
          action === 'submit'
            ? 'hrTime.submitted'
            : action === 'close'
              ? 'hrTime.closedToast'
              : 'hrTime.reopened',
        ),
        { tone: 'success' },
      )
      void queryClient.invalidateQueries({ queryKey: ['time', 'period'] })
    },
    onError: (caught) => {
      if (caught instanceof ApiError && caught.code === 'UNAPPROVED_ENTRIES') {
        setUnapprovedToClose(Number(caught.details.count ?? 0))
        return
      }
      setError('hrTime.errorGeneric')
    },
  })

  if (period.isPending) return <Spinner label={t('common.loading')} />
  if (period.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const data = period.data
  const approvals = pendingEntries.data?.items ?? []
  /* Lista przychodzi posortowana po nazwisku i dacie — grupujemy kolejne wiersze osoby. */
  const groups: { employeeId: string; name: string; entries: TimeApprovalEntry[] }[] = []
  for (const entry of approvals) {
    const last = groups.at(-1)
    if (last?.employeeId === entry.employeeId) last.entries.push(entry)
    else groups.push({ employeeId: entry.employeeId, name: `${entry.firstName} ${entry.lastName}`, entries: [entry] })
  }
  const statusTone = data.status === 'closed' ? 'success' : data.status === 'review' ? 'pink' : 'warning'
  const statusLabel =
    data.status === 'closed'
      ? t('hrTime.periodClosed')
      : data.status === 'review'
        ? t('hrTime.periodReview')
        : t('hrTime.periodOpen')

  return (
    <>
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <div className={styles.periodBar}>
        <span className={styles.monthNav}>
          <Button
            size="sm"
            variant="secondary"
            aria-label={t('hrTime.previousMonth')}
            onClick={() => setMonth(shiftMonth(month, -1))}
          >
            <ChevronLeft size={16} />
          </Button>
          <span className={styles.monthLabel}>{fmt.month(month, 'long')}</span>
          <Button
            size="sm"
            variant="secondary"
            aria-label={t('hrTime.nextMonth')}
            onClick={() => setMonth(shiftMonth(month, 1))}
          >
            <ChevronRight size={16} />
          </Button>
        </span>

        <StatusPill tone={statusTone}>{statusLabel}</StatusPill>

        {/* Okresy zamyka rola payroll (§5.3) — reszta widzi sam stan, bez akcji. */}
        {canClosePeriods ? (
          <span className={styles.periodActions}>
            {data.status === 'open' ? (
              <Button size="sm" variant="secondary" onClick={() => change.mutate({ action: 'submit' })}>
                {t('hrTime.submit')}
              </Button>
            ) : null}
            {data.status !== 'closed' ? (
              <Button size="sm" onClick={() => change.mutate({ action: 'close' })}>
                {t('hrTime.close')}
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => change.mutate({ action: 'reopen' })}>
                {t('hrTime.reopen')}
              </Button>
            )}
          </span>
        ) : null}
      </div>

      {unapprovedToClose !== null ? (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('hrTime.closeUnapproved', { count: unapprovedToClose })}{' '}
          <Button
            size="sm"
            disabled={change.isPending}
            onClick={() => change.mutate({ action: 'close', acknowledge: true })}
          >
            {t('hrTime.closeAnyway')}
          </Button>
        </InfoBanner>
      ) : null}

      <div className={styles.kpiGrid}>
        {/*
          Kafle mówią godzinami z zestawienia silnika — tą samą liczbą, którą
          płaci lista płac — a braki liczą się per osoba (fala 7b, #36).
        */}
        <StatCard
          label={t('hrTime.exceptions.overtime')}
          value={fmt.decimal(data.exceptions.overtimeHours)}
          unit={t('hrTime.unitHours')}
          note={t('hrTime.exceptions.overtimeNote', { count: data.exceptions.overtimePeople })}
        />
        <StatCard
          label={t('hrTime.exceptions.missing')}
          value={data.exceptions.missingDays}
          unit={t('hrTime.unitDays')}
          note={t('hrTime.exceptions.missingNote', { count: data.exceptions.missingPeople })}
        />
        <StatCard
          label={t('hrTime.exceptions.night')}
          value={fmt.decimal(data.exceptions.nightHours)}
          unit={t('hrTime.unitHours')}
          note={t('hrTime.exceptions.nightNote')}
        />
        <StatCard
          label={t('hrTime.exceptions.unapproved')}
          value={data.exceptions.unapprovedEntries}
          unit={t('hrTime.unitEntries')}
          note={t('hrTime.exceptions.unapprovedNote')}
        />
      </div>

      {/*
        Karta „NIEZATWIERDZONE" nie może być ślepym licznikiem — tu jest akcja.
        Listę widzi każdy, kto widzi ekran (przełożony swój zespół, HR cały
        tenant); przycisk akceptacji dostają wyłącznie role z tym uprawnieniem
        (§5.3): przełożony i płace [D28].
      */}
      {approvals.length > 0 ? (
        <section className={styles.approvals}>
          <h3 className={styles.sectionTitle}>{t('hrTime.approvalsTitle')}</h3>
          {/* Lista pogrupowana po osobie z akcją zbiorczą (fala 7b, grupa 135, D103). */}
          <Card pad="flush" className={styles.approvalList}>
            {groups.map((group) => (
              <div key={group.employeeId} className={styles.approvalGroup}>
                <div className={styles.approvalGroupHead}>
                  <p className={styles.approvalName}>{group.name}</p>
                  {canApprove && group.entries.length > 1 ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={approveAll.isPending}
                      onClick={() => approveAll.mutate(group.entries.map((entry) => entry.id))}
                    >
                      {t('hrTime.approveAll', { count: group.entries.length })}
                    </Button>
                  ) : null}
                </div>
                {group.entries.map((entry) => (
                  <div key={entry.id} className={styles.approvalRow}>
                    <div className={styles.approvalBody}>
                      <p className={styles.approvalMeta}>
                        {formatDate(entry.date, 'dot')}
                        {entry.startsAt && entry.endsAt
                          ? ` · ${entry.startsAt.slice(0, 5)}–${entry.endsAt.slice(0, 5)}`
                          : ''}{' '}
                        · {fmt.hours(entry.hours)} ·{' '}
                        {t(`portal.time.kinds.${entry.kind}`, { defaultValue: entry.kind })}
                        {entry.note ? ` · ${entry.note}` : ''}
                      </p>
                    </div>
                    {canApprove ? (
                      <Button
                        size="sm"
                        disabled={approve.isPending}
                        onClick={() => approve.mutate(entry.id)}
                      >
                        {t('hrTime.approveEntry')}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ))}
          </Card>
        </section>
      ) : null}

      <section>
        <div className={styles.calendarHead}>
          <h3 className={styles.sectionTitle}>{t('hrTime.calendarTitle')}</h3>
          <span className={styles.norm}>{t('hrTime.normNote', { hours: data.normHours })}</span>
        </div>

        <Card pad="md">
          <div className={styles.calendar}>
            {WEEKDAYS.map((day) => (
              <span key={day} className={styles.weekday}>
                {t(`hrTime.weekdays.${day}`)}
              </span>
            ))}
            {Array.from({ length: leadingBlanks(data.days[0]?.date ?? `${month}-01`) }, (_, index) => (
              <span key={`blank-${index}`} />
            ))}
            {data.days.map((day) => (
              <div
                key={day.date}
                className={cx(
                  styles.day,
                  (day.weekend || day.holiday) && styles.dayOff,
                  day.missing && styles.dayMissing,
                )}
              >
                <span className={styles.dayNumber}>{Number(day.date.slice(8))}</span>
                <span className={styles.dayHours}>
                  {day.hours > 0 ? fmt.hours(day.hours) : day.weekend || day.holiday ? '—' : ''}
                </span>
                {day.holiday ? <span className={styles.mark}>{t('hrTime.holiday')}</span> : null}
                {day.missing ? (
                  <span className={cx(styles.mark, styles.markMissing)}>
                    {day.missingPeople > 1
                      ? t('hrTime.missingMarkPeople', { count: day.missingPeople })
                      : t('hrTime.missingMark')}
                  </span>
                ) : null}
                {day.nightHours > 0 ? (
                  <span className={styles.mark}>{t('hrTime.nightMark')}</span>
                ) : null}
                {day.overtimeHours > 0 ? (
                  <span className={cx(styles.mark, styles.markOvertime)}>+</span>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      </section>
    </>
  )
}
